import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_001924de-85a6-4169-ab14-49ef05e58c3d/artifacts/qt88pa3s_checkmate%20by%20memarketgroup_1.png";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    
    try {
      const { confirmPassword, ...registerData } = formData;
      const result = await register(registerData);
      
      if (result.status === 'approved') {
        toast.success('Account created! You can now login.');
        navigate('/login');
      } else {
        setSuccess(true);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#10B981]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Registration Submitted!</h1>
          <p className="text-slate-400 mb-8">
            Your registration request has been submitted. You will receive an email once your account is approved by our team.
          </p>
          <Link to="/login">
            <Button className="bg-[#4A90D9] hover:bg-[#3A80C9] text-white" data-testid="back-to-login-btn">
              Back to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1120] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 py-12 lg:px-16">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="mb-8">
            <img src={LOGO_URL} alt="Checkmate" className="h-14 w-auto mb-6" />
            <h1 className="text-3xl font-bold text-white mb-2">Request Access</h1>
            <p className="text-slate-400">Create an account to access the client portal</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-slate-300">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                  className="mt-2 bg-[#1E293B] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#4A90D9]"
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <Label htmlFor="company" className="text-slate-300">Company</Label>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Company Inc."
                  className="mt-2 bg-[#1E293B] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#4A90D9]"
                  data-testid="register-company-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-300">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
                className="mt-2 bg-[#1E293B] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#4A90D9]"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-slate-300">Phone Number</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1 (555) 000-0000"
                className="mt-2 bg-[#1E293B] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#4A90D9]"
                data-testid="register-phone-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-300">Password *</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  required
                  minLength={6}
                  className="bg-[#1E293B] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#4A90D9] pr-10"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password *</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                required
                className="mt-2 bg-[#1E293B] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#4A90D9]"
                data-testid="register-confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#4A90D9] hover:bg-[#3A80C9] text-white py-6 btn-hover"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Submitting...
                </span>
              ) : (
                'Request Access'
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="text-[#4A90D9] hover:text-[#38BDF8] font-medium">
              Sign In
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image/Branding */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A5F] to-[#0B1120]" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center p-12">
            <img src={LOGO_URL} alt="Checkmate" className="h-32 w-auto mx-auto mb-8 animate-pulse-glow" />
            <h2 className="text-4xl font-black text-white mb-4">Checkmate</h2>
            <p className="text-xl text-slate-300">by Me Marketing Group LLC</p>
          </div>
        </div>
      </div>
    </div>
  );
}
